<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useMutation, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import {
  LibraryStepLockSaveToolsMutation,
  LibraryStepLockSaveToolsMutationVariables,
  LibraryStepLockStepQuery,
  LibraryStepLockStepQueryVariables,
  Mutation,
  PermissionOrganization,
  UnlinkLibraryStepMutation,
  UnlinkLibraryStepMutationVariables,
} from '@/generated/graphql'
import ViewStep from '@/components/step/ViewStep.vue'
import ArticleAllocationList from '@/components/article/ArticleAllocationList.vue'
import StepInputsSection from '@/components/step/StepInputsSection.vue'
import StepOutputsSection from '@/components/step/StepOutputsSection.vue'
import ViewCheckConfigurations from '@/components/checkConfiguration/ViewCheckConfigurations.vue'
import ToolPicker from '@/components/input/ToolPicker.vue'
import AsyncDeleteDialog from '@/components/dialogs/AsyncDeleteDialog.vue'
import AcceptLibraryStepUpdate from '@/components/step/AcceptLibraryStepUpdate.vue'
import { ComponentExposed } from 'vue-component-type-helpers'
import useAuthorization from '@/composables/useAuthorization'

const props = defineProps<{
  stepId: string
  moduleId: string
  // Only for a library reference saved before library uses became linked steps.
  graphNodeStepId?: string
  bopId: string
  siteId: string
  isLatestBopVersion: boolean
  canUnlink: boolean
}>()
const emit = defineEmits<{
  unlinked: []
  deleted: []
  updated: [bopId: string]
}>()

const { t } = useI18n()
const router = useRouter()
const { hasOrgAuthz } = useAuthorization()

const libraryHref = computed(
  () =>
    router.resolve({
      name: 'libraryStep',
      params: { stepId: stepQuery.result.value?.product.step.libraryStep?.id ?? props.stepId },
    }).href,
)

const stepQuery = useQuery<LibraryStepLockStepQuery, LibraryStepLockStepQueryVariables>(
  gql`
    query LibraryStepLockStep($id: ID!) {
      product {
        step(id: $id) {
          id
          name
          stepType {
            id
          }
          durationInSeconds
          instructionText
          warningText
          toolIds
          libraryStep {
            id
            libraryItemVersion {
              version
              latestReleasedVersion
              updateAvailable
            }
          }
        }
      }
    }
  `,
  () => ({ id: props.stepId }),
)
const stepName = computed(() => stepQuery.result.value?.product.step.name ?? '')
const libraryItemVersion = computed(
  () => stepQuery.result.value?.product.step.libraryStep?.libraryItemVersion,
)
const updateAvailable = computed(
  () => props.isLatestBopVersion && !!libraryItemVersion.value?.updateAvailable,
)

const toolIds = ref<string[]>([])
watch(
  () => stepQuery.result.value,
  (r) => {
    toolIds.value = r?.product.step.toolIds ?? []
  },
  { immediate: true },
)
const saveToolsMutation = useMutation<
  LibraryStepLockSaveToolsMutation,
  LibraryStepLockSaveToolsMutationVariables
>(gql`
  mutation LibraryStepLockSaveTools($step: UpdateStep!) {
    product {
      updateStep(command: $step) {
        id
      }
    }
  }
`)
// Like any step, a linked step saves its tools together with its content, which stays as the library has it.
const saveTools = (ids: string[] | undefined) => {
  const step = stepQuery.result.value?.product.step
  if (!step) return
  toolIds.value = ids ?? []
  saveToolsMutation.mutate({
    step: {
      id: step.id,
      stepTypeId: step.stepType.id,
      name: step.name,
      durationInSeconds: step.durationInSeconds,
      instructionText: step.instructionText,
      warningText: step.warningText,
      toolIds: toolIds.value,
    },
  })
}

const deleteDialog = ref<ComponentExposed<typeof AsyncDeleteDialog>>()
const removeMutation = useMutation<Mutation, { id: string }>(
  gql`
    mutation RemoveLibraryStepFromModule($id: ID!) {
      product {
        deleteStep(id: $id)
      }
    }
  `,
  { refetchQueries: ['StepTreeData'] },
)
const doRemove = async () => {
  await deleteDialog.value?.open(stepName.value, () =>
    removeMutation.mutate({ id: props.stepId }).then(() => emit('deleted')),
  )
}

const confirmOpen = ref(false)
const unlinkMutation = useMutation<UnlinkLibraryStepMutation, UnlinkLibraryStepMutationVariables>(
  gql`
    mutation UnlinkLibraryStep($command: UnlinkLibraryStep!) {
      product {
        unlinkLibraryStep(command: $command) {
          id
        }
      }
    }
  `,
)
const doUnlink = async () => {
  await unlinkMutation.mutate({ command: { stepId: props.stepId } })
  confirmOpen.value = false
  emit('unlinked')
}
</script>

<template>
  <v-alert
    type="info"
    variant="tonal"
    density="compact"
    border="start"
    icon="lock"
    prominent
    class="mb-4"
  >
    <div class="text-subtitle-2">{{ t('component.libraryStepLock.title') }}</div>
    <p class="mb-3 text-body-2">{{ t('component.libraryStepLock.message') }}</p>
    <div v-if="updateAvailable && libraryItemVersion" class="mb-3 text-body-2">
      <strong>{{ t('component.libraryStepStatus.updateAvailableMessage') }}</strong>
      <div>
        {{
          t('component.libraryStepStatus.versions', {
            current: libraryItemVersion.version,
            latest: libraryItemVersion.latestReleasedVersion,
          })
        }}
      </div>
    </div>
    <div class="d-flex flex-wrap ga-2">
      <accept-library-step-update
        :step-id="stepId"
        :module-id="moduleId"
        :bop-id="bopId"
        :is-latest-bop-version="isLatestBopVersion"
        color="info"
        variant="flat"
        prepend-icon="arrow_circle_up"
        @updated="emit('updated', $event)"
      />
      <v-btn
        color="primary"
        variant="elevated"
        prepend-icon="library_add_check"
        append-icon="open_in_new"
        :href="libraryHref"
        target="_blank"
        rel="noopener"
        class="text-decoration-none"
      >
        {{ t('component.libraryStepLock.editInLibrary') }}
      </v-btn>
      <v-btn
        v-if="canUnlink"
        color="warning"
        variant="outlined"
        prepend-icon="link_off"
        @click="confirmOpen = true"
      >
        {{ t('component.libraryStepLock.unlink') }}
      </v-btn>
    </div>
  </v-alert>

  <h2 style="position: relative">
    {{ stepName }}
    <div v-if="hasOrgAuthz(PermissionOrganization.OrgProductDelete)" class="step-toolbar">
      <v-btn
        icon="delete"
        density="compact"
        variant="flat"
        :loading="removeMutation.loading.value"
        @click="doRemove"
      />
    </div>
  </h2>

  <template v-if="canUnlink">
    <h3 class="mt-4">{{ t('entity.stepInput.plural') }}</h3>
    <step-inputs-section
      :bop-id="bopId"
      :module-id="moduleId"
      :step-id="stepId"
      :graph-node-step-id="graphNodeStepId"
    />

    <h3 class="mt-4">{{ t('entity.article.plural') }}</h3>
    <article-allocation-list
      :label="t('entity.article.plural')"
      :step-id="stepId"
      :bop-id="bopId"
      :graph-node-step-id="graphNodeStepId"
    />

    <h3 class="mt-4">{{ t('entity.stepOutput.plural') }}</h3>
    <step-outputs-section
      :module-id="moduleId"
      :step-id="stepId"
      :graph-node-step-id="graphNodeStepId"
    />

    <h3 class="mt-4">{{ t('entity.step.field.tools') }}</h3>
    <v-card>
      <v-card-text>
        <tool-picker :model-value="toolIds" :site-id="siteId" @update:model-value="saveTools" />
      </v-card-text>
    </v-card>
  </template>

  <view-step
    :step-id="stepId"
    :bop-id="bopId"
    :graph-node-step-id="graphNodeStepId"
    :hide-allocations="canUnlink"
    :hide-inputs="canUnlink"
    :hide-tools="canUnlink"
    hide-title
    hide-font-size
  />

  <h3 class="mt-4">{{ t('entity.checkConfiguration.plural') }}</h3>
  <view-check-configurations :step-id="stepId" />

  <async-delete-dialog ref="deleteDialog" />

  <v-dialog v-model="confirmOpen" width="460">
    <v-card :title="t('component.libraryStepLock.unlinkTitle')">
      <v-card-text>{{ t('component.libraryStepLock.unlinkConfirm') }}</v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="confirmOpen = false">{{ t('button.cancel') }}</v-btn>
        <v-btn
          color="warning"
          variant="elevated"
          :loading="unlinkMutation.loading.value"
          @click="doUnlink"
        >
          {{ t('component.libraryStepLock.unlink') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.step-toolbar {
  position: absolute;
  right: 0;
  top: 0;
}
</style>
